<template>
  <k-modal
    id="project-general-feedback"
    :show="showModal"
    @close="onClose"
    :closeOnClickAway="false"
  >
    <template #header>
        General Feedback for project
    </template>
    <template #body>
      <div class="feedback-container">
        <div class="phase-selector-header">
          <h2 class="section-title">Select Project Phase</h2>
          <span>While editing/creating feedback please select project phase, default is General.
          </span>
          <div class="phase-tabs">
            <button
              v-for="phase in phaseOptions"
              :key="phase.id"
              class="phase-tab"
              :class="getPhaseClasses(phase.id)"
              @click="selectedPhase = phase.id"
            >
              <span class="phase-dot"></span>
              {{ phase.name }}
            </button>
          </div>
        </div>

        <notes-overview
          v-if="showModal"
          :filterable="true"
          :allow-adding-new-notes="true"
          :is-project-feedback="true"
          :is-learner-support="true"
          :use-custom-notes="true"
          :custom-notes="feedbackNotes"
          :editor-max-length="editorMaxLength"
          @custom-get-notes="getFeedback"
          @custom-add-note="addFeedback"
          @custom-edit-note="editFeedback"
          @custom-delete-note="deleteFeedback"
        >
        <template #tags="{ note }">
          <div class="tags">
            <span v-if="note && note.project_phase" :class="note.project_phase">{{ note.project_phase }}</span>
            <k-tooltip text="Project phase at time of feedback" trigger-label="More information about project phase" position="right">
              <i class="fas fa-info-circle" aria-hidden="true"></i>
            </k-tooltip>
          </div>
        </template>
        </notes-overview>
      </div>
    </template>
  </k-modal>
</template>
<style scoped>
.feedback-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.phase-selector-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.phase-tabs {
  display: flex;
  gap: 8px;
  padding: 4px;
  background: var(--kate-background-light);
  border-radius: 8px;
}

.phase-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--kate-type-light);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.phase-tab:hover {
  background: rgb(from var(--kate-type-light-rgb) / 0.05);
}

.phase-tab.active {
  background: var(--kate-note);
  border: 1px solid inherit;
  box-shadow: 0 2px 4px rgb(0 0 0 / 0.05);
}

.phase-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.phase-tab.scoping .phase-dot {
  background-color: var(--kate-monitor);
}

[data-theme="light"] .phase-tab.scoping .phase-dot {
  background-color: var(--kate-type-warning);
}

.phase-tab.general .phase-dot {
  background-color: var(--kate-type-blue);
}

.phase-tab.completed .phase-dot {
  background-color: var(--kate-success);
}

.phase-tab.active.scoping {
  color: var(--kate-monitor);
  border: 1px solid var(--kate-monitor);
}

.phase-tab.active.general {
  color: var(--kate-type-blue);
  border: 1px solid var(--kate-type-blue);
}

.phase-tab.active.completed {
  color: var(--kate-success);
  border: 1px solid var(--kate-success);
}

[data-theme="light"] .phase-tab.active.scoping {
  color: var(--kate-type-dark);
  background-color: #c7760080;
  border: 1px solid var(--kate-type-warning);
}

[data-theme="light"] .phase-tab.active.general {
  color: var(--kate-type-dark);
  background-color: #228ff880;
}

[data-theme="light"] .phase-tab.active.completed {
  color: var(--kate-type-dark);
  background-color: #4caf5080;
}

.tags {
  display: inline-flex;
  gap: 4px;
}

.tags span.scoping {
  background-color: var(--kate-monitor);
}

[data-theme="light"] .tags span.scoping {
  background-color: var(--kate-type-warning);
}

.tags span.general {
  background-color: var(--kate-type-blue);
  color: var(--kate-type-dark);
}

.tags span.completed {
  background-color: var(--kate-success);
  color: var(--kate-type-dark);
}

.scoping::before,
.general::before,
.completed::before,
.evidence::before {
  border-color: transparent var(--current-color) transparent transparent;
}

[data-theme="light"] .scoping::before { --current-color: var(--kate-type-warning); }
.scoping::before { --current-color: var(--kate-monitor); }
.general::before { --current-color: var(--kate-type-blue); }
.completed::before { --current-color: var(--kate-success); }
.evidence::before { --current-color: var(--kate-type-blue); }
</style>
<script>
import KModal from '@base-components/k-modal.vue';
import { KTooltip } from '@kate/components';
import NotesOverview from '../../../student/notes-overview.vue';

export default {
  name: 'ProjectGeneralFeedback',

  components: {
    KModal,
    NotesOverview,
    KTooltip,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    selectedProjectData: {
      type: Object,
    },
    editorMaxLength: {
      type: Number,
      default: 4096,
    },
  },
  data() {
    return {
      feedbackDataReady: false,
      isEditing: false,
      selectedPhase: 'general',
      feedbackNotes: [],
      phaseOptions: [
        { name: 'Scoping', id: 'scoping' },
        { name: 'General', id: 'general' },
        { name: 'Completed', id: 'completed' },
      ],
    };
  },

  watch: {
    showModal(newVal) {
      if (newVal) {
        this.getFeedback();
      }
    },
  },

  computed: {
    getPhaseDisplayName() {
      const phase = this.phaseOptions.find(p => p.id === this.selectedPhase);
      return phase ? phase.name : '';
    },

    getPhaseClasses() {
      return phaseId => ({
        active: this.selectedPhase === phaseId,
        [phaseId]: true,
      });
    },
  },

  methods: {
    async getFeedback() {
      this.$Loading.minimal();
      this.$logger.info('Fetching feedback', { projectId: this.selectedProjectData.projectId });

      try {
        const response = await this.$http.get(`/api/curriculum/admin/programmes/${this.selectedProjectData.programmeId}/projects/${this.selectedProjectData.projectId}/learner/${this.selectedProjectData.studentId}/feedback`);
        this.$logger.info('Successfully fetched feedback', { projectId: this.selectedProjectData.projectId });
        const feedbackData = response.data || [];
        this.feedbackNotes = await this.formatFeedbackToNotes(feedbackData);
      } catch (error) {
        this.$logger.error('Error fetching feedback', { projectId: this.selectedProjectData.projectId }, error);
        this.$ktoast.showError(error, true);
        this.feedbackNotes = [];
      } finally {
        this.$Loading.finish();
      }
    },

    async addFeedback(feedbackData) {
      this.$Loading.minimal();
      this.$logger.info('Adding feedback', { projectId: this.selectedProjectData.projectId });

      try {
        await this.$http.put(`/api/curriculum/admin/programmes/${this.selectedProjectData.programmeId}/projects/${this.selectedProjectData.projectId}/learner/${this.selectedProjectData.studentId}/feedback`, {
          feedback_text: feedbackData,
          project_phase_at_feedback: this.selectedPhase,
        });

        this.$logger.info('Successfully added feedback', { projectId: this.selectedProjectData.projectId });
        this.$ktoast.success('Feedback added successfully');
        await this.getFeedback();
      } catch (error) {
        this.$logger.error('Error adding feedback', { projectId: this.selectedProjectData.projectId }, error);
        this.$ktoast.showError(error, true);
      } finally {
        this.$Loading.finish();
      }
    },

    async editFeedback(feedbackId, feedbackData) {
      this.$Loading.minimal();
      this.$logger.info('Editing feedback', { projectId: this.selectedProjectData.projectId, feedbackId });
      const payload = {
        feedback_id: feedbackId,
        feedback_text: feedbackData,
        project_phase_at_feedback: this.selectedPhase,
      };

      try {
        await this.$http.put(`/api/curriculum/admin/programmes/${this.selectedProjectData.programmeId}/projects/${this.selectedProjectData.projectId}/learner/${this.selectedProjectData.studentId}/feedback`, payload);
        this.$logger.info('Successfully edited feedback', { projectId: this.selectedProjectData.projectId, feedbackId });
        this.$ktoast.success('Feedback updated successfully');
        await this.getFeedback();
      } catch (error) {
        this.$logger.error('Error editing feedback', { projectId: this.selectedProjectData.projectId, feedbackId }, error);
        this.$ktoast.showError(error, true);
      } finally {
        this.$Loading.finish();
      }
    },

    async deleteFeedback(feedbackId) {
      this.$Loading.minimal();
      this.$logger.info('Deleting feedback', { projectId: this.selectedProjectData.projectId, feedbackId });

      try {
        await this.$http.delete(`/api/curriculum/admin/programmes/${this.selectedProjectData.programmeId}/projects/${this.selectedProjectData.projectId}/learner/${this.selectedProjectData.studentId}/feedback/${feedbackId}`);
        this.$logger.info('Feedback deleted successfully', { projectId: this.selectedProjectData.projectId, feedbackId });
        this.$ktoast.success('Feedback deleted successfully');
        await this.getFeedback();
      } catch (error) {
        this.$logger.error('Error deleting feedback', { projectId: this.selectedProjectData.projectId, feedbackId }, error);
        this.$ktoast.showError(error, true);
      } finally {
        this.$Loading.finish();
      }
    },

    async formatFeedbackToNotes(feedback) {
      if (!Array.isArray(feedback) || feedback.length === 0) {
        return [];
      }
      const userIds = [...new Set(feedback.map(f => f.author_user_id))];
      const authorNames = await this.fetchUserNames(userIds);
      return this.transformFeedbackToNotes(feedback, authorNames);
    },

    async fetchUserNames(userIds) {
      const authorNames = {};
      await Promise.all(
        userIds.map(async userId => {
          authorNames[userId] = await this.fetchUserName(userId);
        }),
      );
      return authorNames;
    },

    async fetchUserName(userId) {
      try {
        const res = await this.$http.get(`/api/profile/users/${userId}`);
        return res.data.full_name;
      } catch (error) {
        this.$logger.warn('Failed to fetch user name', { userId }, error);
        return 'Mentor';
      }
    },

    transformFeedbackToNotes(feedback, authorNames) {
      return feedback.map(feedbackItem => ({
        note_id: feedbackItem.id,
        programme_member_id: null,
        note: feedbackItem.feedback_text,
        date_created: feedbackItem.created_at,
        date_updated: null,
        author_user_id: feedbackItem.author_user_id,
        author_name: authorNames[feedbackItem.author_user_id] || 'Mentor',
        share_with_employer: false,
        source: 'project_feedback',
        project_phase: feedbackItem.project_phase_at_feedback,
      }));
    },

    onClose() {
      this.$emit('close');
    },
  },
};
</script>
